import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import type {
  StaffBatchDTO,
  StaffChecksDTO,
  StaffHomeDTO,
  StaffKeyDTO,
  StaffLaundryDTO,
  StaffLostDTO,
  StaffMeDTO,
  StaffPlacesDTO,
  StaffRepairDetailDTO,
  StaffRepairsDTO,
  StaffRunDTO,
} from "@shared/types";
import { can, type StaffPermission } from "@shared/domain";
import { api, ApiError } from "@/lib/api";
import { API_ROOT, endpoints } from "@/lib/endpoints";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/queryKeys";
import { usePreviewMode, usePreviewModeStore } from "./preview/mode";

/*
 * The staff app's reads. Everything goes to /api/staff, which answers in
 * staff-safe shapes; nothing here touches a desk endpoint. Live: a real
 * phone refetches on focus and every 30 seconds while in front; the Plan's
 * phone view every 5 seconds, and at once on a kk:refresh message.
 */

/** 401s are the shell's to handle (back to sign-in), not the desk's session sheet. */
export const staffGet = <T,>(path: string) => api<T>(path, { quiet401: true });

function useLive() {
  const preview = usePreviewMode();
  return { refetchInterval: preview ? 5_000 : 30_000, refetchIntervalInBackground: false, refetchOnWindowFocus: true } as const;
}

type Opts<T> = Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, "queryKey" | "queryFn">;

function useStaffQuery<T>(key: readonly unknown[], path: string, opts: Opts<T> = {}) {
  const live = useLive();
  return useQuery<T, Error, T, readonly unknown[]>({ queryKey: key, queryFn: () => staffGet<T>(path), ...live, ...opts });
}

export const loadStaffMe = () => queryClient.ensureQueryData({ queryKey: queryKeys.staff.me, queryFn: () => staffGet<StaffMeDTO>(endpoints.staff.me) });

export function useStaffMe() {
  return useQuery({ queryKey: queryKeys.staff.me, queryFn: () => staffGet<StaffMeDTO>(endpoints.staff.me), refetchOnWindowFocus: true, staleTime: 15_000 });
}

/** Signed in, inside the staff shell. */
export function useStaffUser(): StaffMeDTO {
  const { data } = useStaffMe();
  if (!data) throw new Error("useStaffUser outside the staff shell");
  return data;
}

export function useStaffCan(...any: StaffPermission[]): boolean {
  const { data } = useStaffMe();
  return !!data && any.some((p) => can({ role: "staff", grants: data.grants }, p));
}

export const useStaffHome = () => useStaffQuery<StaffHomeDTO>(queryKeys.staff.home, endpoints.staff.home);
export const useStaffChecks = () => useStaffQuery<StaffChecksDTO>(queryKeys.staff.checks, endpoints.staff.checks);
export const useStaffRun = (id: string) => useStaffQuery<StaffRunDTO>(queryKeys.staff.check(id), endpoints.staff.check(id), { refetchInterval: false });
export const useStaffRepairs = () => useStaffQuery<StaffRepairsDTO>(queryKeys.staff.repairs, endpoints.staff.repairs);
export const useStaffRepair = (id: string) => useStaffQuery<StaffRepairDetailDTO>(queryKeys.staff.repair(id), endpoints.staff.repair(id));
export const useStaffPlaces = () => useStaffQuery<StaffPlacesDTO>(queryKeys.staff.places, endpoints.staff.places, { refetchInterval: false, staleTime: 5 * 60_000 });
export const useStaffLaundry = () => useStaffQuery<StaffLaundryDTO>(queryKeys.staff.laundry, endpoints.staff.laundry);
export const useStaffBatch = (id: string) => useStaffQuery<StaffBatchDTO>(queryKeys.staff.batch(id), endpoints.staff.batch(id), { refetchInterval: false });
export const useStaffKeys = () => useStaffQuery<StaffKeyDTO[]>(queryKeys.staff.keys, endpoints.staff.keys);
export const useStaffLost = () => useStaffQuery<StaffLostDTO[]>(queryKeys.staff.lost, endpoints.staff.lost);

/** After any write: everything the staff app shows, again. */
export const refreshStaff = () => queryClient.invalidateQueries({ queryKey: queryKeys.staff.all });

/** A grant taken away while the screen was open. */
export const isBlocked = (e: unknown) => e instanceof ApiError && e.status === 403;
/** Gone: not theirs, or no longer there. */
export const isGone = (e: unknown) => e instanceof ApiError && e.status === 404;

/** A photo's address for an <img>, which can't send the preview header. */
export const staffFileUrl = (id: string) => `${API_ROOT}${endpoints.staff.file(id)}${usePreviewModeStore.getState().on ? "?kkapp=preview" : ""}`;
